import * as React from 'react'
import * as reactRedux from 'react-redux'
import * as MainTemplate from '../templates/Main'
import * as SignInTemplate from '../templates/SignIn'
import { ethers } from 'ethers'
import { Progress } from '~/entity'
import * as ToastActions from '~store/ui/toast'
import { abi as v1ABI } from '~modules/v1abi'
import { abi as v2ABI } from '~modules/v2abi'

type Provider = ethers.providers.Web3Provider
type Network = ethers.providers.Network
type Contract = ethers.Contract

declare global {
    interface Window {
        ethereum: any | undefined // metamask injects global ethereum
    }
}

type State = {
    connected: boolean
    address?: string
}
const defaultProgressState = {
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    7: false,
    8: false
}

export const Component = () => {
    const dispatch = reactRedux.useDispatch()
    const [pageState, setState] = React.useState<State>({ connected: false })
    const [progress, setProgress] = React.useState<Progress>(
        defaultProgressState
    )
    const [pixelNumber, setPixelNumber] = React.useState<number | undefined>()
    const [_provider, setProvider] = React.useState<Provider | null>(null)
    const [_signer, setSigner] = React.useState<ethers.Signer | null>(null)
    const [_network, setNetwork] = React.useState<Network | null>(null)
    const [v1Contract, setV1Contract] = React.useState<Contract | null>(null)
    const [v2Contract, setV2Contract] = React.useState<Contract | null>(null)

    const connect = async () => {
        try {
            await attemptConnection()
            window.ethereum.on('accountsChanged', () => {
                return attemptConnection()
            })
        } catch (error) {
            console.error(error)
            alert(error.message)
        }
    }

    const attemptConnection = async () => {
        if (window.ethereum === undefined) {
            throw Error('MetaMask not found, please visit https://metamask.io/')
        }
        // make sure page refreshes when network is changed
        // https://github.com/MetaMask/metamask-extension/issues/8226
        window.ethereum.on('chainIdChanged', () => window.location.reload())
        window.ethereum.on('chainChanged', () => window.location.reload())

        // get provider, address, and network
        const eProvider = new ethers.providers.Web3Provider(window.ethereum)
        await eProvider.send('eth_requestAccounts', [])
        const eSigner = await eProvider.getSigner()
        const address = await eSigner.getAddress()
        const eNetwork = await eProvider.getNetwork()
        // TODO update address
        const v1Contract = new ethers.Contract(
            '0xc0d72d45cca854e0f2fe3cd2d4bab91e772fe4c0',
            v1ABI,
            eSigner
        )
        const v2Contract = new ethers.Contract(
            '0x6f9d53ba6c16fcbe66695e860e72a92581b58aed',
            v2ABI,
            eSigner
        )

        // set page states
        setProvider(eProvider)
        setNetwork(eNetwork)
        setSigner(eSigner)
        setState({
            connected: true,
            address: address
        })
        setV1Contract(v1Contract)
        setV2Contract(v2Contract)
    }

    // component マウント後に一度だけ呼ばれる
    // すでにこのサイトの許可済の場合には自動でコネクトする
    React.useEffect(() => {
        if (window.ethereum) {
            window.ethereum
                .request({
                    method: 'eth_accounts'
                })
                .then((r: Array<any>) => {
                    if (r.length > 0) return connect()
                    return
                })
        }
    }, [])

    const onClickConnect = async () => {
        try {
            console.log('here called')
            await connect()
        } catch (e) {
            alert(e)
        }
    }

    const onChangePixelNumber = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            if (isNaN(Number(event.target.value))) {
                console.log('not a number')
            } else if (Number(event.target.value) > 9999) {
                dispatch(
                    ToastActions.append({
                        label: 'Invalid',
                        text: 'Invalid Pixel Number!',
                        variant: 'error'
                    })
                )
                setPixelNumber(0)
            } else {
                setPixelNumber(Number(event.target.value))
            }
            setProgress(defaultProgressState)
        },
        [dispatch]
    )

    const onClickStepButton = (step: number) => {
        if (step === 0) {
            return async () => {
                if (!v1Contract || !v2Contract) return

                if (!pixelNumber) {
                    dispatch(
                        ToastActions.append({
                            label: 'Error',
                            text: 'Input your pixel number first!',
                            variant: 'error'
                        })
                    )
                    return
                }

                let owner
                try {
                    owner = await v2Contract.ownerOf(pixelNumber)
                } catch (e) {}

                if (
                    owner &&
                    ethers.utils.getAddress(owner) ===
                        ethers.utils.getAddress(pageState.address as string)
                ) {
                    setProgress({
                        ...progress,
                        0: true,
                        1: true,
                        2: true,
                        3: true,
                        4: true,
                        5: true,
                        6: true,
                        7: true,
                        8: true
                    })
                    dispatch(
                        ToastActions.append({
                            label: 'INFO',
                            text: 'You already wrapped!',
                            variant: 'success'
                        })
                    )
                    return
                }

                const [addressV1] = await v1Contract.getPixel(pixelNumber)
                if (
                    ethers.utils.getAddress(addressV1) ===
                    ethers.utils.getAddress(v2Contract.address)
                ) {
                    setProgress({
                        ...progress,
                        1: true,
                        2: true,
                        3: true,
                        4: true,
                        5: true,
                        6: true
                    })
                    dispatch(
                        ToastActions.append({
                            label: 'INFO',
                            text:
                                'Pixel owner is already v2 contract. Did you reload this page? Maybe you can go to step 7 if you are the owner.',
                            variant: 'warning'
                        })
                    )
                    return
                }

                const [address] = await v2Contract.getPixel(pixelNumber)
                if (
                    ethers.utils.getAddress(address) !==
                    ethers.utils.getAddress(pageState.address as string)
                ) {
                    dispatch(
                        ToastActions.append({
                            label: 'Invalid',
                            text: 'Pixel is not yours!',
                            variant: 'error'
                        })
                    )
                    return
                }

                const preparedAddress = await v2Contract.preparedCarefulWrapOwners(
                    pixelNumber
                )
                if (
                    ethers.utils.getAddress(preparedAddress) ===
                    ethers.utils.getAddress(pageState.address as string)
                ) {
                    setProgress({
                        ...progress,
                        0: true,
                        1: true,
                        2: true,
                        3: true,
                        4: true
                    })
                    dispatch(
                        ToastActions.append({
                            label: 'OK',
                            text: 'Go to step 5!',
                            variant: 'success'
                        })
                    )
                    return
                }

                setProgress({
                    0: true
                })
                dispatch(
                    ToastActions.append({
                        label: 'OK',
                        text: 'Go to step 1!',
                        variant: 'success'
                    })
                )
            }
        }
        if (step === 1) {
            return async () => {
                if (!v1Contract || !v2Contract || !pixelNumber) return
                const [address, , , isSale] = await v2Contract.getPixel(
                    pixelNumber
                )
                if (
                    ethers.utils.getAddress(address) !==
                    ethers.utils.getAddress(pageState.address as string)
                ) {
                    dispatch(
                        ToastActions.append({
                            label: 'Invalid',
                            text: 'Pixel is not yours!',
                            variant: 'error'
                        })
                    )
                    return
                }

                setProgress({
                    ...progress,
                    1: true,
                    2: !isSale
                })
                const message = isSale
                    ? 'Success but your pixel sale state is true. You need to do step 2 to change it false.'
                    : 'Success and you can skip step 2! Go to step 3!'
                dispatch(
                    ToastActions.append({
                        label: 'OK',
                        text: message,
                        variant: 'success'
                    })
                )
            }
        }
        if (step === 2) {
            return async () => {
                if (!v1Contract || !pixelNumber) return
                try {
                    const { hash } = await v1Contract.setSaleState(
                        pixelNumber,
                        false
                    )
                    setProgress({
                        ...progress,
                        2: true
                    })
                    // todo change url to main
                    const url = `https://etherscan.io/tx/${hash}`
                    dispatch(
                        ToastActions.append({
                            label:
                                'OK: Wait until the transaction is succeeded!',
                            text: url,
                            variant: 'success'
                        })
                    )
                } catch (err) {
                    dispatch(
                        ToastActions.append({
                            label: 'Error',
                            text:
                                'Something unexpected happened! try it again from step 0!',
                            variant: 'error'
                        })
                    )
                }
            }
        }
        if (step === 3) {
            return async () => {
                if (!v2Contract || !pixelNumber) return
                const preparedAddress = await v2Contract.preparedCarefulWrapOwners(
                    pixelNumber
                )
                if (
                    preparedAddress &&
                    ethers.utils.getAddress(preparedAddress) ===
                        ethers.utils.getAddress(pageState.address as string)
                ) {
                    setProgress({
                        ...progress,
                        3: true
                    })
                    dispatch(
                        ToastActions.append({
                            label: 'OK',
                            text: 'You already on the list. Go to step 4!',
                            variant: 'success'
                        })
                    )
                    return
                }

                try {
                    const { hash } = await v2Contract.prepareCarefulWrap(
                        pixelNumber
                    )
                    setProgress({
                        ...progress,
                        3: true
                    })
                    // todo change url to main
                    const url = `https://etherscan.io/tx/${hash}`
                    dispatch(
                        ToastActions.append({
                            label:
                                'OK: Wait until the transaction is succeeded!',
                            text: url,
                            variant: 'success'
                        })
                    )
                } catch (err) {
                    dispatch(
                        ToastActions.append({
                            label: 'Error',
                            text:
                                'Something unexpected happened! try it again from step 0!',
                            variant: 'error'
                        })
                    )
                }
            }
        }
        if (step === 4) {
            return async () => {
                if (!v2Contract || !pixelNumber) return
                const preparedAddress = await v2Contract.preparedCarefulWrapOwners(
                    pixelNumber
                )
                if (
                    ethers.utils.getAddress(preparedAddress) ===
                    ethers.utils.getAddress(pageState.address as string)
                ) {
                    setProgress({
                        ...progress,
                        4: true
                    })
                    dispatch(
                        ToastActions.append({
                            label: 'OK',
                            text: 'Go to step 5!',
                            variant: 'success'
                        })
                    )
                    return
                } else {
                    dispatch(
                        ToastActions.append({
                            label: 'Hmm',
                            text:
                                'You are not yet on the list. Please check your transaction is succeeded!',
                            variant: 'warning'
                        })
                    )
                }
            }
        }
        if (step === 5) {
            return async () => {
                if (!v1Contract || !pixelNumber || !v2Contract) return

                const [address, , ,] = await v2Contract.getPixel(pixelNumber)
                if (
                    ethers.utils.getAddress(address) ===
                    ethers.utils.getAddress(v2Contract.address)
                ) {
                    setProgress({
                        ...progress,
                        5: true
                    })
                    dispatch(
                        ToastActions.append({
                            label: 'OK',
                            text:
                                'You already set the owner to v2 contract address!',
                            variant: 'success'
                        })
                    )
                    return
                }

                const { hash } = await v1Contract.setOwner(
                    pixelNumber,
                    v2Contract.address
                )
                console.log(hash)
                setProgress({
                    ...progress,
                    5: true
                })
                // todo change url to main
                const url = `https://etherscan.io/tx/${hash}`
                dispatch(
                    ToastActions.append({
                        label: 'OK: Wait until the transaction is succeeded!',
                        text: url,
                        variant: 'success'
                    })
                )
            }
        }
        if (step === 6) {
            return async () => {
                if (!v1Contract || !pixelNumber || !v2Contract) return

                const [address, , ,] = await v2Contract.getPixel(pixelNumber)
                if (
                    ethers.utils.getAddress(address) ===
                    ethers.utils.getAddress(v2Contract.address)
                ) {
                    setProgress({
                        ...progress,
                        6: true
                    })
                    dispatch(
                        ToastActions.append({
                            label: 'OK',
                            text:
                                'Go to step 7! Its almost done!! Stay graceful!',
                            variant: 'success'
                        })
                    )
                    return
                } else {
                    dispatch(
                        ToastActions.append({
                            label: 'Hmm',
                            text:
                                'The ownership is not yet changed to v2 contract address. Please check your transaction is succeeded!',
                            variant: 'warning'
                        })
                    )
                }
            }
        }
        if (step === 7) {
            return async () => {
                if (!v1Contract || !pixelNumber || !v2Contract) return

                try {
                    const owner = await v2Contract.ownerOf(pixelNumber)
                    if (owner === pageState.address) {
                        setProgress({
                            ...progress,
                            7: true,
                            8: true
                        })
                        dispatch(
                            ToastActions.append({
                                label: 'OK',
                                text: 'You already wrapped it!',
                                variant: 'success'
                            })
                        )
                        return
                    } else {
                        const preparedAddress = await v2Contract.preparedCarefulWrapOwners(
                            pixelNumber
                        )
                        if (
                            ethers.utils.getAddress(preparedAddress) !==
                            ethers.utils.getAddress(pageState.address as string)
                        ) {
                            dispatch(
                                ToastActions.append({
                                    label: 'Error',
                                    text:
                                        'You may be the wrong owner. Check your pixel number again and restart from step 0.',
                                    variant: 'warning'
                                })
                            )
                        }
                    }
                } catch (err) {
                    console.log(err)
                }

                const { hash } = await v2Contract.carefulWrap(pixelNumber)
                setProgress({
                    ...progress,
                    7: true
                })
                // todo change url to main
                const url = `https://etherscan.io/tx/${hash}`
                dispatch(
                    ToastActions.append({
                        label: 'OK: Wait until the transaction is succeeded!',
                        text: url,
                        variant: 'success'
                    })
                )
            }
        }
        if (step === 8) {
            return async () => {
                if (!v1Contract || !pixelNumber || !v2Contract) return
                const owner = await v2Contract.ownerOf(pixelNumber)
                if (
                    owner &&
                    ethers.utils.getAddress(owner) ===
                        ethers.utils.getAddress(pageState.address as string)
                ) {
                    setProgress({
                        ...progress,
                        8: true
                    })
                    dispatch(
                        ToastActions.append({
                            label: 'YOU DID IT!',
                            text: 'Every step has been done successfully!!!!!',
                            variant: 'success'
                        })
                    )
                    return
                } else {
                    dispatch(
                        ToastActions.append({
                            label: 'Hmm',
                            text:
                                'Your pixel has not yet been wrapped. Please check your transaction is succeeded!',
                            variant: 'warning'
                        })
                    )
                }
            }
        }
        return () => {
            window.open(
                `https://opensea.io/assets/0x6f9d53ba6c16fcbe66695e860e72a92581b58aed/${pixelNumber}`,
                '_blank'
            )
        }
    }

    if (!pageState.connected) {
        return <SignInTemplate.Component onClickConnect={onClickConnect} />
    } else {
        return (
            <MainTemplate.Component
                address={pageState?.address}
                onClickSign={onClickConnect}
                pixelNumber={pixelNumber}
                progress={progress}
                onChangePixelNumber={onChangePixelNumber}
                onClickStepButton={onClickStepButton}
            />
        )
    }
}
